import {Alert, Button, FormField, showNotification} from 'platform/components';
import {Box, Hide, Show, Stack, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {isEmpty, isNil} from 'ramda';
import {isTrue} from 'ramda-adjunct';

import {suffixTestId, TestIdProps, useBoolean, useRequiredParams} from 'shared/web';

import {validateVin} from 'features/vin-validate';

import {useCatalogue} from '../../../hooks/useCatalogue';
import i18n from '../../../i18n/i18n';
import {
  useGetRequestQuery,
  useLazyGetRequestVinDetailsPaidQuery,
  useLazyGetRequestVinDetailsQuery,
  useUpdateRequestMutation,
} from '../../../store/priceReportApi';
import {DRIVE_4X2} from '../constants/drive4x2';
import {DRIVE_4X4} from '../constants/drive4x4';
import {EXCLUDED_FUEL_TYPES} from '../constants/excludedFuelTypes';
import {MAX_SEAT_COUNT} from '../constants/maxSeatCount';
import {MIN_FIRST_REGISTRATION_YEAR} from '../constants/minFirstRegistrationYear';
import {useGetRequestVehicleFromFormData} from '../utils/useGetRequestVehicleFromFormData';
import {useCreatePageContext} from './CreatePageContext';

interface VinParserProps extends TestIdProps {
  onDecodeSuccess: () => void;
}

export function VinParser(props: VinParserProps) {
  const {requestId} = useRequiredParams();
  const getRequestVehicleFromFormData = useGetRequestVehicleFromFormData();
  const {formApi, control, values} = useCreatePageContext();
  const {getFuelType} = useCatalogue();

  const {data: request} = useGetRequestQuery({id: requestId ?? ''});
  const [updateRequest] = useUpdateRequestMutation();

  const [isLoading, startLoading, finishLoading] = useBoolean(false);
  const [lastDecodeCall, setLastDecodeCall] = useState<'free' | 'paid' | null>(null);
  const [getRequestVinDetails, {isError: isErrorFree, data: dataFree}] =
    useLazyGetRequestVinDetailsQuery();
  const [getRequestVinDetailsPaid, {isError: isErrorPaid, data: dataPaid}] =
    useLazyGetRequestVinDetailsPaidQuery();
  const isError = isErrorFree || isErrorPaid;
  const data = match(lastDecodeCall)
    .with('free', () => dataFree)
    .with('paid', () => dataPaid)
    .otherwise(() => null);

  const handleDecodeVin = async (isPaid: boolean = false) => {
    startLoading();
    const vin = values.vin;

    if (isNil(vin) || isEmpty(vin)) {
      formApi.setError('vin', {
        message: i18n.t('page.create.validation.fieldIsRequired'),
      });
      finishLoading();
      return;
    }

    if (!validateVin(vin)) {
      formApi.setError('vin', {
        message: i18n.t('page.create.validation.vinIsInvalid'),
      });
      finishLoading();
      return;
    }

    const decodedData = await (isPaid ? getRequestVinDetailsPaid : getRequestVinDetails)({
      id: requestId,
    })
      .unwrap()
      .catch(() => showNotification.error(i18n.t('general.labels.error')));
    setLastDecodeCall(isPaid ? 'paid' : 'free');

    if (decodedData?.make) {
      formApi.setValue('make', decodedData.make);
    }
    if (decodedData?.modelFamily) {
      formApi.setValue('modelFamily', decodedData.modelFamily);
    }
    if (decodedData?.firstRegistrationOnYear) {
      const year = parseInt(decodedData.firstRegistrationOnYear);
      if (year >= MIN_FIRST_REGISTRATION_YEAR) {
        formApi.setValue('firstRegistrationOnYear', year);
      } else {
        formApi.setError('firstRegistrationOnYear', {
          message: i18n.t('page.create.validation.firstRegistrationOnYearMustBeAtLeast', {
            year: MIN_FIRST_REGISTRATION_YEAR,
          }),
        });
      }
    }
    if (decodedData?.transmission) {
      formApi.setValue('transmission', [decodedData.transmission]);
    }
    if (decodedData?.fuelType) {
      if (!EXCLUDED_FUEL_TYPES.includes(decodedData.fuelType)) {
        formApi.setValue('fuelType', [decodedData.fuelType]);
      } else {
        formApi.setError('fuelType', {
          message: i18n.t('page.create.validation.fuelTypeMustBeOneOfOptions', {
            value: getFuelType(decodedData.fuelType),
          }),
        });
      }
    }
    if (decodedData?.bodyStyle) {
      formApi.setValue('bodyStyle', [decodedData.bodyStyle]);
    }
    if (decodedData?.drive) {
      formApi.setValue('drive', [decodedData.drive === DRIVE_4X4 ? DRIVE_4X4 : DRIVE_4X2]);
    }
    if (decodedData?.power) {
      formApi.setValue('power', parseInt(decodedData.power));
    }
    if (decodedData?.maximalPower) {
      formApi.setValue('maxPower', parseInt(decodedData.maximalPower));
    }
    if (decodedData?.exteriorColor) {
      formApi.setValue('exteriorColor', [decodedData.exteriorColor]);
    }
    if (decodedData?.seatCount) {
      const seatCount = Number(decodedData.seatCount);
      if (seatCount >= 1 && seatCount <= MAX_SEAT_COUNT) {
        formApi.setValue('seatCount', [String(seatCount)]);
      } else {
        formApi.setError('seatCount', {
          message: i18n.t('page.create.validation.seatCountMustBeBetween', {
            min: 1,
            max: MAX_SEAT_COUNT,
          }),
        });
      }
    }
    if (decodedData?.doorCount) {
      formApi.setValue('doorCount', [decodedData.doorCount]);
    }
    if (decodedData?.features) {
      formApi.setValue('features', decodedData.features);
    }

    if (decodedData) {
      control.onChange();
      props.onDecodeSuccess();
    }

    await updateRequest({
      id: requestId,
      body: {vehicle: getRequestVehicleFromFormData(values)},
    })
      .unwrap()
      .catch(() => showNotification.error(i18n.t('general.labels.error')));

    finishLoading();
  };

  const isDecodedSuccesfully =
    data?.decodingStatus === 'partially' || data?.decodingStatus === 'fully';
  const isDecodedUnsuccesfully = data?.decodingStatus === 'none' || isError;

  return (
    <VStack spacing={6}>
      <Stack
        direction={['column', 'column', 'row', 'row']}
        spacing={6}
        align={['stretch', 'stretch', 'flex-end']}
      >
        <Box flex={1}>
          <FormField
            control={control}
            type="text"
            name="vin"
            label={`${i18n.t('page.create.labels.vin')} (${i18n.t('general.labels.optional')})`}
            onChange={() => formApi.clearErrors('vin')}
            data-testid={suffixTestId('vin', props)}
          />
        </Box>
        <Hide when={request?.additionalSettings?.hideVinDecoder}>
          <Button
            title={i18n.t('page.create.labels.loadDataByVIN')}
            onClick={() => handleDecodeVin(false)}
            isLoading={isLoading}
            isDisabled={isTrue(request?.vinDecoded)}
            data-testid={suffixTestId('loadDataByVIN', props)}
          />
        </Hide>
        <Show when={request?.additionalSettings?.showVinDecoderPaid}>
          <Button
            title={i18n.t('page.create.labels.loadDataByVINPaid')}
            onClick={() => handleDecodeVin(true)}
            isLoading={isLoading}
            isDisabled={isTrue(request?.vinDecodedPaid)}
            data-testid={suffixTestId('loadDataByVINPaid', props)}
          />
        </Show>
      </Stack>
      <Show when={isDecodedSuccesfully}>
        <Alert
          type="inline"
          variant="warning"
          title={i18n.t('page.create.labels.vinCodeRecognized')}
          message={i18n.t('page.create.labels.vinCodeRecognizedMessage')}
          data-testid={suffixTestId('vinCodeRecognized', props)}
        />
      </Show>
      <Show when={isDecodedUnsuccesfully}>
        <Alert
          type="inline"
          variant="warning"
          title={i18n.t('page.create.labels.vinCodeNotRecognized')}
          message={i18n.t('page.create.labels.vinCodeNotRecognizedMessage')}
          data-testid={suffixTestId('vinCodeNotRecognized', props)}
        />
      </Show>
    </VStack>
  );
}
